import React, { useMemo, createContext } from "react";
import SharedMarkdownRenderer from "@photon/chat-react/markdown-renderer";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import TableWithCopy from "./TableWithCopy";
import CodeBlock from "./CodeBlock";
import MdAnchor from "./MdAnchor";
import MdImage from "./MdImage";

export const TableCellContext = createContext(false);

export const MARKDOWN_REMARK_PLUGINS = [
  remarkGfm,
  [remarkMath, {
    singleDollarTextMath: false,  // Disable $...$ to avoid currency conflicts
  }],
];

export const MARKDOWN_REHYPE_PLUGINS = [rehypeKatex];

const MarkdownRenderer = ({ content }) => {
  const components = useMemo(() => ({
    h1: ({ children }) => (
      <h1 className="text-2xl font-bold my-4">{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-xl font-bold my-4">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-lg font-bold my-4">{children}</h3>
    ),
    h4: ({ children }) => (
      <h4 className="text-base font-bold my-4">{children}</h4>
    ),
    h5: ({ children }) => (
      <h5 className="text-sm font-bold my-4">{children}</h5>
    ),
    h6: ({ children }) => (
      <h6 className="text-xs font-bold my-4">{children}</h6>
    ),
    a: ({ href, children }) => <MdAnchor href={href}>{children}</MdAnchor>,
    img: ({ node, ...props }) => <MdImage {...props} />,
    p: ({ children }) => {
      // Check if the paragraph contains components that render div elements
      const childrenArray = React.Children.toArray(children);

      // Helper function to check if a child might render block elements
      const containsBlockElements = (child) => {
        if (!React.isValidElement(child)) return false;

        // Check for specific components that render div elements
        const componentName = child.type?.name || child.type?.displayName;
        if (componentName === 'MdAnchor' || componentName === 'DownloadLinkBox' || componentName === 'MdImage') {
          return true;
        }

        // Check for images (which are rendered as MdImage components with div wrappers)
        if (child.type === MdImage) {
          return true;
        }

        // Additional check for any component that might have image-related props
        if (child.props?.src || child.props?.alt !== undefined) {
          return true;
        }

        // Check for download-related content
        const href = child.props?.href;
        const childrenText = React.Children.toArray(child.props?.children || []).join("").toLowerCase();

        return (href && (
          href.includes(".ipynb") ||
          href.includes(".csv") ||
          href.includes(".xlsx") ||
          href.includes(".pdf") ||
          href.includes("download")
        )) || (childrenText && (
          childrenText.includes("download") ||
          childrenText.includes(".ipynb") ||
          childrenText.includes(".csv") ||
          childrenText.includes(".xlsx") ||
          childrenText.includes(".pdf") ||
          childrenText.includes("notebook")
        ));
      };

      // Check if any child might render block elements
      const hasBlockElements = childrenArray.some(containsBlockElements);

      // If it contains block elements, render as a div instead of p to avoid nesting issues
      if (hasBlockElements) {
        return <div className="my-2 break-words">{children}</div>;
      }

      return <p className="my-2 break-words">{children}</p>;
    },
    code: (props) => <CodeBlock {...props} inline />,
    pre: ({ children }) => (
      <CodeBlock {...React.Children.only(children).props} inline={false} />
    ),
    table: ({ children }) => <TableWithCopy>{children}</TableWithCopy>,
    th: ({ children }) => (
      <th className="px-4 py-2 text-left text-sm font-semibold text-ink-gray-700 dark:text-ink-gray-200 bg-surface-table-header border-b border-edge-gray-200 dark:border-edge-gray-700 border-r last:border-r-0 first:rounded-tl-lg last:rounded-tr-lg break-words whitespace-normal">
        <TableCellContext.Provider value={true}>{children}</TableCellContext.Provider>
      </th>
    ),
    td: ({ children }) => (
      <td className="px-4 py-2 text-sm text-ink-gray-800 dark:text-ink-gray-200 border-b border-edge-gray-200 dark:border-edge-gray-700 border-r last:border-r-0 break-words whitespace-normal">
        <TableCellContext.Provider value={true}>{children}</TableCellContext.Provider>
      </td>
    ),
    ul: ({ children }) => (
      <ul className="revert-list-style ml-5 sm:ml-6 my-2 space-y-1 break-words">
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol className="revert-list-style ml-5 sm:ml-6 my-2 space-y-1 break-words">
        {children}
      </ol>
    ),
    li: ({ children }) => <li className="revert-list-style break-words">{children}</li>,
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-edge-border dark:border-edge-muted-control pl-3 italic text-ink-gray-600 dark:text-ink-gray-300 my-3 break-words">
        {children}
      </blockquote>
    ),
    hr: () => (
      <hr className="border-t border-edge-hex-e5e5e5 dark:border-edge-hex-373c44 my-3" />
    ),
  }), []);
  return (
    <SharedMarkdownRenderer
      content={content}
      className="max-w-full font-inter my-2 overflow-x-auto"
      remarkPlugins={MARKDOWN_REMARK_PLUGINS}
      rehypePlugins={MARKDOWN_REHYPE_PLUGINS}
      components={components}
    />
  );
};

export default React.memo(MarkdownRenderer);
